import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useEffect, useRef } from "react";
import { Menu, X, Sun, Moon, Phone, MessageCircle, ChevronDown } from "lucide-react";
import * as CommonActions from "../../redux/actions/commonAction";
import * as PartnerActions from "../../redux/actions/partnerAction";
import { image_urls } from "../../utils/api-urls";
import { support_number } from "../../utils/constant";

const NavLinksData = [
    { name: 'Home', path: '#home' },
    { name: 'About', path: '#about' },
    { name: 'Reports', path: '#reports' },
    { name: 'Benefits', path: '#benefits' },
    { name: 'Business Opportunity', path: '#business' },
    { name: 'FAQ', path: '#faq' },
];

const PartnerHeader = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isScreenScroll } = useSelector(state => state?.commonReducer);
    const { partnerDetails } = useSelector(state => state?.partnerReducer);

    const navRef = useRef(null);
    const [shownav, setShownav] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

    useEffect(() => {
        if (theme === "dark") { document.documentElement.classList.add("dark"); }
        else { document.documentElement.classList.remove("dark"); }
        localStorage.setItem("theme", theme);
    }, [theme]);

    const toggleTheme = () => setTheme(p => p === "light" ? "dark" : "light");

    // Auto-fetch partner details if not already loaded
    const searchParams = new URLSearchParams(location.search);
    const urlPartnerId = searchParams.get('partnerId') || localStorage.getItem('partnerId') || '';
    
    useEffect(() => {
        if (urlPartnerId && (!partnerDetails || Object.keys(partnerDetails).length === 0)) {
            dispatch(PartnerActions.getPartnerDetails({ partnerId: urlPartnerId }));
        }
    }, [urlPartnerId]);


    useEffect(() => {
        if (partnerDetails === undefined) return;
        if (partnerDetails === null) return;
        if (Object.keys(partnerDetails).length === 0) return;

        if (
            partnerDetails?.partner_name &&
            partnerDetails?.company_name &&
            partnerDetails?.registration_number
        ) {
            const query = new URLSearchParams({
                partnerId: partnerDetails.registration_number,
                name: partnerDetails.partner_name,
                company: partnerDetails.company_name,
            }).toString();
            navigate(`${location.pathname}?${query}`, { replace: true });
        }
    }, [partnerDetails]);

    useEffect(() => {
        const handleResize = () => { if (window.innerWidth > 500) { setShownav(false); } };
        const handleScroll = () => { dispatch(CommonActions.setScreenScroll(window.scrollY > 10)); };
        window.addEventListener("resize", handleResize);
        document.addEventListener("scroll", handleScroll);
        return () => { window.removeEventListener("resize", handleResize); document.removeEventListener("scroll", handleScroll); };
    }, []);

    const handleNavClick = (e, path) => {
        e.preventDefault();
        if (path.startsWith('#')) {
            const sectionId = path.substring(1);
            if (sectionId === 'home') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                const element = document.getElementById(sectionId);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }
        } else {
            // For external routes, preserve partner query params
            navigate(`${path}${location.search}`);
        }
        setIsMenuOpen(false);
    };

    return (
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScreenScroll ? "bg-[#0f0d1a] shadow-md" : "bg-[#0f0d1a]/90 backdrop-blur-md shadow-sm"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">

                    <div className="flex items-center gap-2 cursor-pointer" onClick={(e) => handleNavClick(e, '#home')}>
                        <img src={partnerDetails ? image_urls + partnerDetails?.business_logo : "/logo.png"} alt="logo" className="w-10 h-10" />
                        <span className={`text-lg font-semibold transition text-white hover:text-[#8bcf6a]`}>{partnerDetails ? partnerDetails?.partner_name : 'Partner'}</span>
                    </div>

                    <nav className="hidden lg:flex items-center gap-8">
                        {NavLinksData.map(link => (
                            <a key={link?.name} href={link?.path} onClick={(e) => handleNavClick(e, link?.path)} className={`flex items-center gap-1 font-medium transition cursor-pointer text-white hover:text-[#8bcf6a]`}>
                                {link?.name}
                            </a>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-4">
                        <button onClick={toggleTheme} className={`p-2 rounded-full transition text-white hover:bg-white/10`}>
                            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </button>
                        <a href={`tel:${partnerDetails?.mobile_number || support_number}`} className={`flex items-center gap-2 font-medium transition text-white`}>
                            <Phone className="w-4 h-4" />
                            <span>{partnerDetails?.mobile_number || support_number}</span>
                        </a>
                        <a
                            href={`https://wa.me/${(partnerDetails?.mobile_number || support_number).replace(/[^0-9+]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow hover:scale-105 transition"
                        >
                            <MessageCircle className="w-5 h-5" />
                        </a>
                    </div>

                    <div className="flex lg:hidden items-center gap-2">
                        <button onClick={toggleTheme} className={`p-2 rounded-full transition text-white hover:bg-white/10`}>
                            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </button>
                        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className={`p-2 rounded-full transition text-white hover:bg-white/10`}>
                            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {isMenuOpen && (
                    <div className="lg:hidden py-4 bg-[#004d46] border-t border-white/10">
                        <nav className="flex flex-col gap-4">
                            {NavLinksData.map(link => (
                                <a key={link?.name} href={link?.path} onClick={(e) => handleNavClick(e, link?.path)} className="text-white/90 hover:text-[#8bcf6a] text-sm cursor-pointer">
                                    {link?.name}
                                </a>
                            ))}
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
}

export default PartnerHeader;
